import { Fragment, useRef, useState } from 'react';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { createForm, useCreateFormStore } from '@components/form';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconWebhook } from '@components/icons/webhook';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import { useIntegrationWebhook, useUpdateIntegrationWebhook } from 'api/integrations/hooks';
import { IntegrationWebhook, WebhookCondition } from 'api/integrations/requests';
import { useLists } from 'api/lists/hooks';
import { List } from 'api/lists/requests';
import { Condition } from 'api/types';
import { ConditionComponent, getComparatorIds } from 'ui/actions/condition-v2/helpers';

export const useIntegrationWebhookConditionsSidebar = () =>
  useSidebar(IntegrationWebhookConditionsSidebar, { variant: 'REGULAR' });

const Form = createForm();

const IntegrationWebhookConditionsSidebar = ({ webhookId }: { webhookId: string }) => {
  const { t } = useScopedTranslate(locale);
  const webhookQuery = useIntegrationWebhook(webhookId);
  const webhook = webhookQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconAddPlus}
        iconColor="elementBlue100"
        title={t('addConditions')}
      />
      <Sidebar.Content>
        {!webhook || webhookQuery.isLoading ? (
          <RelativeSpinner />
        ) : (
          <Conditions webhook={webhook} />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

export const useWebhookPayloadVariableGroup = ({
  id,
  webhook,
}: {
  id: string;
  webhook?: IntegrationWebhook;
}) => {
  const { t } = useScopedTranslate(locale);
  const { data: lists } = useLists();

  const getOptions = (optionsFrom: string) => {
    if (!optionsFrom) return [];
    return (lists ?? ([] as List[])).find((list) => list.id === optionsFrom)?.options ?? [];
  };

  const variableGroups = [
    {
      id: id,
      label: t('payloadValues'),
      icon: <IconWebhook />,
      description: '',
      fields: (webhook?.payloadVariables ?? [])
        .filter((variable) => variable.type !== 'file' && variable.type !== 'array')
        .map((variable) => ({
          action: id,
          field: variable.name,
          label: variable.label,
          source: 'action',
          type: variable.type,
          variable: `${id}__${variable.name}`,
          ...(variable.type === 'select' &&
            variable.options_from && {
              options: getOptions(variable.options_from ?? ''),
            }),
        })),
    },
  ] as FieldMappingGroup[];
  return variableGroups;
};

const Conditions = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const updateMutation = useUpdateIntegrationWebhook();
  const formStore = useCreateFormStore();
  const initialConditions = useRef((webhook.conditions ?? []) as Condition[]);
  const [conditions, setConditions] = useState<Condition[]>(
    initialConditions.current.map((condition) => ({ ...condition, id: condition.id ?? uuid() }))
  );
  const isDirty = formStore.useIsDirty() || initialConditions.current.length !== conditions.length;
  const formValues = formStore.useFormValues();
  const variableGroups = useWebhookPayloadVariableGroup({ id: webhook.shortId, webhook });

  const handleSubmit = async () => {
    const formConditions = conditions.map((condition) => {
      const { elementId, comparatorId, valueId } = getComparatorIds(condition.id);
      const value = formValues[valueId];
      return {
        id: condition.id,
        field: formValues[elementId],
        comparator: formValues[comparatorId],
        value,
        visible: Boolean(!value),
      } as Condition;
    });
    await updateMutation.mutateAsync({
      shortId: webhook.shortId,
      data: {
        conditions: formConditions as WebhookCondition[],
      },
    });
    hideSidebar();
  };

  return (
    <Form store={formStore} onSubmit={handleSubmit}>
      <Stack space={4}>
        <Alert variant="info">
          <Text text={t('infoText')} font="regular14" color="indigo100" />
        </Alert>
        {conditions.map((condition, idx) => (
          <Fragment key={condition.id}>
            {idx > 0 && (
              <Stack space={2} orientation="horizontal" justify="center" align="center">
                <Separator />
                <Text
                  font="semibold14"
                  style={{ textTransform: 'uppercase' }}
                  color="textSecondary"
                  text={t('core.and')}
                />
                <Separator />
              </Stack>
            )}
            <ConditionComponent
              isWebhook
              key={condition.id}
              variableGroups={variableGroups}
              onRemove={() => setConditions(conditions.filter((c) => c.id !== condition.id))}
              condition={condition}
              setConditions={setConditions}
              form={Form}
              fieldMappingId={`webhook-${webhook.shortId}`}
            />
          </Fragment>
        ))}
        <Stack orientation="horizontal">
          <Button
            onClick={() =>
              setConditions([
                ...conditions,
                {
                  id: uuid(),
                  field: { value: '' },
                  comparator: undefined,
                  value: undefined,
                },
              ])
            }
            text={t('addElement')}
            iconLeft={<IconAddPlus />}
            variant="secondary"
          />
        </Stack>
      </Stack>

      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button variant="secondary" text={t('core.cancel')} onClick={hideSidebar} />
          <Form.Submit
            data-cy="action-save-btn"
            variant="primary-blue"
            text={t('core.save')}
            isDisabled={!isDirty}
          />
        </ButtonGroup>
      </Sidebar.Footer>
    </Form>
  );
};

const locale = {
  en: {
    addConditions: 'Add Conditions',
    payloadValues: 'Payload values',
    addElement: 'Add element',
    infoText:
      "Add conditions that will be applied to all actions using this webhook. You can add the value yourself if it's the same for all actions like 'status is completed' or select to have the value added in the journey like 'id' that is traveller specific.",
  },
};
